// OrderDetail Page - Detailed view of a single order
import { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Package, Truck, Phone, Wrench, CheckCircle, XCircle, AlertCircle, Loader2, User, Copy, Check, ShoppingCart, ChevronDown, ChevronUp, PhoneCall, MapPin, ExternalLink } from 'lucide-react'
import clsx from 'clsx'
import * as Dialog from '@radix-ui/react-dialog'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { EngineerLayout } from '../../components/layout/EngineerLayout'
import { Button, useToast } from '../../components/radix'
import { OrderSubmissionStatus } from '../../components/OrderSubmissionStatus'
import { SupplierOrderNumber } from '../../components/engineer/SupplierOrderNumber'
import { CancelOrderSection } from '../../components/engineer/CancelOrder/CancelOrderSection'
import { CancelRequestSheet } from '../../components/engineer/CancelOrder/CancelRequestSheet'
import { CancelRequestCard } from '../../components/engineer/CancelOrder/CancelRequestCard'
import { MarkCancelledDialog } from '../../components/engineer/CancelOrder/MarkCancelledDialog'
import { useOrder } from '../../auth/hooks/useOrders'
import { useMe } from '../../auth/hooks/useMe'
import { retryOrder, refreshOrderStatus, markOrderManualStatus, withdrawOrder, reorder, getOrderActivity, requestOrderCancellation, markOrderCancelled } from '../../auth/services/auth-api'
import type { CancellationReason } from '../../auth/services/auth-api'
import { mapOrderDetailForEngineer } from '../../services/order-mappers'
import { getStatusLabel } from '../../constants/orderStatus'
import { EMPTY_VALUE } from '../../constants/display'
import { googleMapsDirectionsUrl } from '../../utils/maps'
// formatDateTime (comma style, e.g. "21 Jun 2026, 16:05") is used only for
// activity timestamps, matching the approver's log; the rest of this page keeps
// the engineer-mobile "at" style.
import { formatDateTimeAt, formatDate, formatDateTime as formatActivityTimestamp } from '../../utils/datetime'
import { formatShortDate, toDateKey } from '../../utils/fulfillmentDates'
import { formatUnitPrice } from '../../utils/currency'
import type { EngineerOrder } from '../../types'

interface EngineerSession {
  postcode: string
  latitude: number
  longitude: number
}

// Activity events shown before the engineer taps "Show all activity".
const COLLAPSED_ACTIVITY_COUNT = 3

// Statuses that allow cancellation
const CANCELLABLE_STATUSES: EngineerOrder['status'][] = ['pending', 'processing', 'awaiting_approval']

// Format date with time - UK timezone (BST/GMT)
const formatDateTime = formatDateTimeAt

/** Per-item availability shown to the engineer after approval (APR-011). */
interface ItemAvailability {
  kind: 'oos' | 'changed' | 'ready'
  /** Absolute date the item is now expected (e.g. "29 Jul 2026"). */
  label: string
  /** Original date the engineer saw at checkout, when it changed. */
  original?: string
}

/** Statuses where the order never went ahead, so per-item availability is moot. */
const HIDE_ITEM_AVAILABILITY_STATUSES: EngineerOrder['status'][] = [
  'fulfillment_declined',
  'supplier_cancelled',
]

/**
 * Statuses where the collection-SLA hint (and the "call before you go" line,
 * and the supplier-ref "quote our order number" hint) is
 * stale/nonsensical:
 *   - order not yet with the supplier (awaiting approval, being submitted, retrying)
 *   - order finished, cancelled, declined, or never went out
 * `awaiting_supplier` means submission SUCCEEDED — supplier has the order, so the SLA applies.
 */
const SLA_HIDE_STATUSES: EngineerOrder['status'][] = [
  'awaiting_approval',
  'approved',
  'started_processing',
  'submission_failed',
  'completed',
  'cancelled',
  'internal_decline',
  'fulfillment_declined',
  'supplier_cancelled',
  'withdrawn',
]

/** Statuses where the order never went ahead, so it has no collection or delivery date. */
const FULFILLMENT_DATE_HIDE_STATUSES: EngineerOrder['status'][] = [
  'submission_failed',
  'cancelled',
  'internal_decline',
  'fulfillment_declined',
  'supplier_cancelled',
  'withdrawn',
]

/**
 * Statuses where the branch doesn't have the order (never sent, or not sent
 * yet), so there is nothing to ring the branch about — the Call branch button
 * is hidden. Once the supplier has it, the button stays, including after the
 * order finishes (a completed or supplier-declined order is still worth a call).
 */
const NOT_WITH_BRANCH_STATUSES: EngineerOrder['status'][] = [
  'awaiting_approval',
  'approved',
  'started_processing',
  'submission_failed',
  'internal_decline',
  'withdrawn',
]

/**
 * Resolve the post-approval availability line for an order item.
 *
 * Out of stock (AC3); a changed date vs the one captured at checkout, shown as
 * original vs new (AC1); or normal availability for an unchanged item (AC2).
 * Dates are absolute (e.g. "29 Jul 2026"), never "Today"/"Tomorrow": this is a
 * persisted record that may be reopened days later, when a relative label would
 * be wrong. A same-day shift counts as unchanged (edge case). Returns null only
 * when there is no post-approval date at all — pre-approval, or the supplier
 * gave no usable date — so the caller can hide the line.
 */
function getItemAvailability(item: {
  placedQuantity?: number | null
  unavailableAtDecision?: boolean
  expectedDateAtOrder?: string | null
  expectedDateAtSubmission?: string | null
}): ItemAvailability | null {
  // Out of stock: nothing placed at approval (AC3), or the decline snapshot
  // recorded 0 orderable stock for this line (AC7).
  if (item.placedQuantity === 0 || item.unavailableAtDecision) {
    return { kind: 'oos', label: 'Out of stock' }
  }
  const submissionDate = item.expectedDateAtSubmission
  // No post-approval date (pre-approval, or supplier gave no date) → nothing.
  if (!submissionDate) return null
  if (Number.isNaN(new Date(submissionDate).getTime())) return null
  const label = formatDate(submissionDate)
  // A change needs a captured checkout baseline AND a different calendar day.
  const orderDate = item.expectedDateAtOrder
  const changed =
    !!orderDate && orderDate.slice(0, 10) !== submissionDate.slice(0, 10)
  if (changed) {
    return {
      kind: 'changed',
      label,
      original: formatDate(orderDate),
    }
  }
  // Unchanged — show normal availability (AC2).
  return { kind: 'ready', label }
}

/**
 * One-line branch address with the UK postcode kept together (mock's
 * addressLine), e.g. "12 High St, Leeds, LS1 1AA".
 */
function formatAddressLine(address: string, postcode: string): string {
  const unbreakablePostcode = postcode.trim().replace(/\s+/g, ' ')
  return [address.trim(), unbreakablePostcode].filter(Boolean).join(', ')
}

/** Map-pin circle, bold branch name and grey one-line address (mock's branch row). */
function BranchRowContent({ name, address }: { name: string; address: string }) {
  return (
    <>
      <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-gray-100">
        <MapPin className="h-4 w-4 text-gray-600" aria-hidden />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold text-gray-900 break-words">{name}</span>
        <span className="sr-only">, </span>
        {address && <span className="block text-xs text-gray-500 break-words">{address}</span>}
      </span>
    </>
  )
}

export function OrderDetailPage() {
  const navigate = useNavigate()
  const { tenantSlug, orderId } = useParams()
  const [session, setSession] = useState<EngineerSession | null>(null)
  const [showContactManagerModal, setShowContactManagerModal] = useState(false)
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [showWithdrawDialog, setShowWithdrawDialog] = useState(false)
  const [withdrawError, setWithdrawError] = useState<string | null>(null)
  // CANCEL-002: engineer-initiated cancellation request. The sheet is driven
  // by the mutation below; `cancelSendFailed` is a separate flag so a failed
  // send keeps the sheet open on the Try again state (AC7).
  const [showCancelSheet, setShowCancelSheet] = useState(false)
  const [cancelSendFailed, setCancelSendFailed] = useState(false)
  const [focusCancelCard, setFocusCancelCard] = useState(false)
  // CANCEL-004: confirm dialog for "Branch confirmed? Mark as cancelled".
  const [showMarkCancelled, setShowMarkCancelled] = useState(false)
  const [copied, setCopied] = useState(false)
  const [hasInvalidatedCart, setHasInvalidatedCart] = useState(false)
  const [isRetryPolling, setIsRetryPolling] = useState(false)
  const [retryStartAttempts, setRetryStartAttempts] = useState(0)

  const queryClient = useQueryClient()
  const { addToast } = useToast()

  // Fetch order from backend
  const { data: backendOrder, isLoading, error } = useOrder(orderId!)
  const order = backendOrder ? mapOrderDetailForEngineer(backendOrder) : null

  // Activity log — the same timeline the approver reads, minus internal staff
  // notes (filtered backend-side). No permission gate is needed: the endpoint
  // is scoped to orders this user can already see, so an engineer only ever
  // gets their own. Mirrors the approver page's cache settings (30s stale,
  // refetch on focus) so a supplier transition picked up on refocus refreshes
  // the status and the timeline in the same beat.
  const {
    data: activity,
    isLoading: activityLoading,
    error: activityError,
  } = useQuery({
    queryKey: ['order-activity', orderId],
    queryFn: () => getOrderActivity(orderId!),
    enabled: !!orderId,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  })
  const hasBackendOrder = !!backendOrder
  const [showAllActivity, setShowAllActivity] = useState(false)
  // Newest first for display; the API returns oldest -> newest (canonical).
  const activityEvents = [...(activity?.results ?? [])].reverse()
  const visibleActivityEvents = showAllActivity
    ? activityEvents
    : activityEvents.slice(0, COLLAPSED_ACTIVITY_COUNT)

  // Manual status update - only available when the supplier has no live status
  // API and the current user is the engineer who placed the order.
  const { data: meData } = useMe()
  const [manualStatusAction, setManualStatusAction] =
    useState<'completed' | 'fulfillment_declined' | null>(null)
  const [manualStatusError, setManualStatusError] = useState<string | null>(null)

  const isOrderPlacer = !!meData?.id && meData.id === backendOrder?.user_id
  const canManuallyUpdateStatus =
    backendOrder?.status === 'processing' &&
    backendOrder?.status_available === false &&
    isOrderPlacer

  // Bold "call before you go" line above the Call branch button, for suppliers
  // that never tell us an order is ready (backend decides which), only while
  // the supplier has the order and it's still open.
  const showCallBranchPrompt =
    backendOrder?.call_branch_prompt === true &&
    !!order &&
    !SLA_HIDE_STATUSES.includes(order.status)

  // The supplier has the order and it's still open — the only time "quote our
  // order number at the branch" is sound advice (same status rule as above).
  const isOpenWithSupplier = !!order && !SLA_HIDE_STATUSES.includes(order.status)

  // Branch row (mock design): "{Supplier} {Branch}" over a one-line address,
  // linking to Google Maps directions — to the branch found by name + address
  // (its own listing), or its coordinates when it has no address. On every order.
  const branchRowName = order ? `${order.supplierName} ${order.branchName}` : ''
  const branchAddressLine = order?.branch
    ? formatAddressLine(order.branch.address, order.branch.postcode)
    : ''
  const branchDirectionsUrl = order?.branch
    ? googleMapsDirectionsUrl({
        name: branchRowName,
        address: order.branch.address,
        postcode: order.branch.postcode,
        latitude: order.branch.latitude,
        longitude: order.branch.longitude,
      })
    : null

  // DEV-51011: the day booked with the supplier (the engineer's pick, or the
  // earliest moved to an open day), as on the order confirmation page.
  const bookedDateKey = order ? toDateKey(order.expectedFulfillmentDate) : null

  const manualStatusMutation = useMutation({
    mutationFn: ({ id, newStatus }: { id: string; newStatus: 'completed' | 'fulfillment_declined' }) =>
      markOrderManualStatus(id, newStatus),
    onSuccess: async () => {
      setManualStatusError(null)
      // Wait for the order data to actually refresh before closing the
      // modal - keeps the spinner visible across both the API call and the
      // cache refetch so the user never sees a momentary flash of stale UI.
      await queryClient.invalidateQueries({ queryKey: ['orders', orderId] })
      await queryClient.invalidateQueries({ queryKey: ['orders'] })
      await queryClient.invalidateQueries({ queryKey: ['order-activity', orderId] })
      setManualStatusAction(null)
    },
    onError: (err) => {
      const data = (err as { data?: { error?: string } })?.data
      setManualStatusError(data?.error || 'Failed to update order status. Please try again.')
    },
  })

  // Withdraw an own order while it is awaiting approval (APR-007). The row is
  // locked backend-side, so if the approver acts first this returns 409 and we
  // just refresh to show the resolved status.
  const withdrawMutation = useMutation({
    mutationFn: (id: string) => withdrawOrder(id),
    onSuccess: async () => {
      setWithdrawError(null)
      await queryClient.invalidateQueries({ queryKey: ['orders', orderId] })
      await queryClient.invalidateQueries({ queryKey: ['orders'] })
      await queryClient.invalidateQueries({ queryKey: ['weekly-spend'] })
      await queryClient.invalidateQueries({ queryKey: ['order-activity', orderId] })
      setShowWithdrawDialog(false)
    },
    onError: async (err) => {
      const errorResponse = err as { status?: number; data?: { error?: string } }
      // 409 = the approver resolved the order first. Refresh the cached detail
      // and list so the stale approval card and Withdraw button drop away and
      // the true (approved/declined) status shows. Also refresh weekly spend:
      // a decline moves the order out of awaiting_approval (counted) into
      // internal_decline (excluded), so a stale cache would overstate spend.
      if (errorResponse?.status === 409) {
        await queryClient.invalidateQueries({ queryKey: ['orders', orderId] })
        await queryClient.invalidateQueries({ queryKey: ['orders'] })
        await queryClient.invalidateQueries({ queryKey: ['weekly-spend'] })
        await queryClient.invalidateQueries({ queryKey: ['order-activity', orderId] })
      }
      setWithdrawError(errorResponse?.data?.error || 'Could not withdraw this order. Please refresh and try again.')
    },
  })

  // Request cancellation (CANCEL-002). On success the sheet closes and a
  // "Cancellation requested" card renders under the status area (AC8); the
  // persisted request means the Request cancellation button goes away on
  // reload (AC9). On failure the sheet stays open with Try again (AC7).
  const requestCancellationMutation = useMutation({
    mutationFn: ({ reason, note }: { reason: CancellationReason; note: string }) =>
      requestOrderCancellation(orderId!, { reason, note }),
    onSuccess: async () => {
      setCancelSendFailed(false)
      setShowCancelSheet(false)
      setFocusCancelCard(true)
      await queryClient.invalidateQueries({ queryKey: ['orders', orderId] })
      await queryClient.invalidateQueries({ queryKey: ['orders'] })
      await queryClient.invalidateQueries({ queryKey: ['order-activity', orderId] })
    },
    onError: async (err) => {
      const errorResponse = err as { status?: number; data?: { error?: string; code?: string } }
      // 409 means the order is no longer cancellable (someone's beaten us to
      // it, or the branch moved it on). Refresh so the stale cancel section
      // drops away. The failure UI only applies to real send errors.
      if (errorResponse?.status === 409) {
        await queryClient.invalidateQueries({ queryKey: ['orders', orderId] })
        await queryClient.invalidateQueries({ queryKey: ['orders'] })
        setShowCancelSheet(false)
        addToast(
          errorResponse?.data?.error || 'This order can no longer be cancelled.',
          'warning'
        )
        return
      }
      setCancelSendFailed(true)
    },
  })

  // Mark order cancelled (CANCEL-004). Only valid after a cancellation
  // request has been sent and the branch has confirmed off-platform. Flips
  // the internal status to CANCELLED (sticky — later supplier updates can't
  // override it, see STICKY_ADMIN_TERMINAL_STATUSES).
  const markCancelledMutation = useMutation({
    mutationFn: () => markOrderCancelled(orderId!),
    onSuccess: async () => {
      setShowMarkCancelled(false)
      setFocusCancelCard(true)
      await queryClient.invalidateQueries({ queryKey: ['orders', orderId] })
      await queryClient.invalidateQueries({ queryKey: ['orders'] })
      await queryClient.invalidateQueries({ queryKey: ['order-activity', orderId] })
    },
    onError: async (err) => {
      const errorResponse = err as { status?: number; data?: { error?: string } }
      addToast(
        errorResponse?.data?.error || 'Could not mark this order as cancelled.',
        'error'
      )
      setShowMarkCancelled(false)
      // 409 almost certainly means the status moved — refresh to show truth.
      if (errorResponse?.status === 409) {
        await queryClient.invalidateQueries({ queryKey: ['orders', orderId] })
        await queryClient.invalidateQueries({ queryKey: ['orders'] })
      }
    },
  })

  // Reorder (APR-006): add every item from this order to the basket. Quantities
  // come from the original order; the backend prices each item fresh and flags
  // any it can't price/source. On success we refresh the cart and go to it.
  const reorderMutation = useMutation({
    mutationFn: (id: string) =>
      reorder(id, session ? { latitude: session.latitude, longitude: session.longitude } : undefined),
    onSuccess: async (result) => {
      const { added, unavailable, skipped, branch_changed_to } = result.data
      await queryClient.invalidateQueries({ queryKey: ['cart'] })
      if (added === 0) {
        addToast('No items could be added to your basket.', 'warning')
        return
      }
      const notes: string[] = []
      if (branch_changed_to) notes.push(`moved to ${branch_changed_to}`)
      if (unavailable.length > 0) notes.push(`${unavailable.length} without a price`)
      if (skipped.length > 0) notes.push(`${skipped.length} couldn't be added`)
      const suffix = notes.length > 0 ? ` · ${notes.join(' · ')}` : ''
      addToast(
        `${added} item${added === 1 ? '' : 's'} added to your basket${suffix}`,
        'success',
      )
      navigate(`/${tenantSlug}/engineer/cart`)
    },
    onError: () => {
      addToast('Could not add items to your basket. Please try again.', 'error')
    },
  })

  // Copy a shareable summary so the engineer can nudge an approver (APR-006
  // AC2/AC3): the job reference (when set), the order reference, and the APPROVER
  // order-detail link (not this engineer page) so whoever it's shared with lands
  // where they can review and approve. The supplier's own reference
  // (supplier_order_id) is intentionally NOT copied — this is the engineer-facing
  // order, not the supplier transaction.
  const handleCopyOrder = async () => {
    if (!order) return
    const lines: string[] = []
    if (order.jobNumber) {
      lines.push(`Job number: ${order.jobNumber}`)
    }
    lines.push(`Order ID: ${order.orderNumber}`)
    lines.push(`Order Link: ${window.location.origin}/${tenantSlug}/orders/${orderId}`)
    try {
      await navigator.clipboard.writeText(lines.join('\n'))
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard unavailable (insecure context / denied permission) — no-op.
    }
  }

  // Determine if order is in active status (should continue polling)
  const isActiveOrder = backendOrder && [
    'started_processing',
    'awaiting_supplier',
    'processing',
    'ready_to_collect',
    'out_for_delivery',
  ].includes(backendOrder.status)

  // `started_processing` is a brief hand-off state (seconds) so poll it
  // fast to catch the transition to `awaiting_supplier` promptly. Once the
  // order is actually waiting on the supplier, fall back to the slower
  // cadence - that wait can legitimately last minutes and 3s polling would
  // just be wasted traffic.
  const isHandingOffToSupplier =
    backendOrder?.status === 'started_processing'
  const pollIntervalMs = isHandingOffToSupplier ? 3000 : 30000

  // Statuses where supplier has accepted and cart items should be cleared
  const isSupplierAccepted = backendOrder && [
    'processing',
    'ready_to_collect',
    'out_for_delivery',
    'delivered',
    'completed',
  ].includes(backendOrder.status)

  // Invalidate cart when order is accepted by supplier
  useEffect(() => {
    if (isSupplierAccepted && !hasInvalidatedCart) {
      queryClient.invalidateQueries({ queryKey: ['cart'] })
      setHasInvalidatedCart(true)
      console.log('[OrderDetail] Order accepted by supplier, invalidating cart cache')
    }
  }, [isSupplierAccepted, hasInvalidatedCart, queryClient])

  // Poll while the order is active. Interval tightens to 3s during the
  // supplier-submission window so the UI reflects "accepted" quickly,
  // and relaxes to 30s once the order is in a longer-lived state.
  useEffect(() => {
    if (!isActiveOrder) return

    const interval = setInterval(() => {
      queryClient.invalidateQueries({ queryKey: ['orders', orderId] })
    }, pollIntervalMs)

    return () => clearInterval(interval)
  }, [isActiveOrder, pollIntervalMs, orderId, queryClient])

  // On page load, trigger a live supplier status check. The backend routes
  // to the correct supplier adapter (Wolseley, CPS, UKPS, etc.) based on
  // the order's supplier_id, updates the DB if the status changed, and
  // caches the result for 5 minutes so rapid refreshes skip the supplier call.
  // If the supplier API is down, the backend returns the current DB state so
  // the page still loads. Errors (unsubmitted order, terminal status) are
  // silently ignored. Existing setInterval polling runs independently and is
  // unaffected by this one-shot mount call.
  //
  // Gated on the detail query having resolved: refresh_status calls
  // get_object() and hits the same scoping rules as the GET, so firing both in
  // parallel on mount just produced a second identical 404 for any order the
  // user can't see. The ref keeps it to one call per order — the .then()
  // invalidates the query this effect depends on, which would otherwise loop.
  const refreshedOrderIdRef = useRef<string | null>(null)
  useEffect(() => {
    if (!orderId || !backendOrder) return
    if (refreshedOrderIdRef.current === orderId) return
    refreshedOrderIdRef.current = orderId

    refreshOrderStatus(orderId)
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ['orders', orderId] })
      })
      .catch(() => {
        // Intentionally silent - backend returns 400 for orders not yet
        // submitted to supplier or in terminal status. Existing polling
        // continues to handle live updates for active orders.
      })
  }, [orderId, backendOrder, queryClient])

  // Retry order mutation
  const retryMutation = useMutation({
    mutationFn: (id: string) => retryOrder(id),
    onSuccess: () => {
      // Start fast polling to catch status change
      setRetryStartAttempts(backendOrder?.submission_attempts || 0)
      setIsRetryPolling(true)
      queryClient.invalidateQueries({ queryKey: ['orders', orderId] })
      queryClient.invalidateQueries({ queryKey: ['order-activity', orderId] })
    },
  })

  // Fast polling after retry - poll every 3s for 60s, then stop
  useEffect(() => {
    if (!isRetryPolling) return

    const interval = setInterval(() => {
      queryClient.invalidateQueries({ queryKey: ['orders', orderId] })
    }, 3000)

    const timeout = setTimeout(() => {
      setIsRetryPolling(false)
    }, 60000)

    return () => {
      clearInterval(interval)
      clearTimeout(timeout)
    }
  }, [isRetryPolling, orderId, queryClient])

  // Stop fast polling when submission succeeds or fails again
  useEffect(() => {
    if (!isRetryPolling) return
    // Stop if supplier accepted the order (has order ID)
    if (backendOrder?.supplier_order_id) {
      setIsRetryPolling(false)
      return
    }
    // Stop if submission attempts increased (retry ran and failed again)
    const currentAttempts = backendOrder?.submission_attempts || 0
    if (currentAttempts > retryStartAttempts) {
      setIsRetryPolling(false)
      return
    }
    // Stop if status moved to a terminal state (completed, cancelled, etc.)
    const status = backendOrder?.status
    if (
      status &&
      status !== 'started_processing' &&
      status !== 'awaiting_supplier' &&
      status !== 'submission_failed'
    ) {
      setIsRetryPolling(false)
    }
  }, [backendOrder?.status, backendOrder?.supplier_order_id, backendOrder?.submission_attempts, isRetryPolling, retryStartAttempts])

  // Keep the timeline in step with the status shown above it without polling
  // the log as well: the order itself is already polled while active, so the
  // log is refreshed only when a field a new event could come from actually
  // changes. Without this the page could show a new status over a stale
  // timeline until the next window focus.
  const activitySignature = [
    backendOrder?.status ?? '',
    backendOrder?.supplier_order_id ?? '',
    backendOrder?.submission_attempts ?? 0,
  ].join('|')
  const lastActivitySignatureRef = useRef<string | null>(null)
  useEffect(() => {
    if (!orderId || !hasBackendOrder) return
    // First resolved order — the query already fetched the current log.
    if (lastActivitySignatureRef.current === null) {
      lastActivitySignatureRef.current = activitySignature
      return
    }
    if (lastActivitySignatureRef.current === activitySignature) return
    lastActivitySignatureRef.current = activitySignature
    queryClient.invalidateQueries({ queryKey: ['order-activity', orderId] })
  }, [activitySignature, hasBackendOrder, orderId, queryClient])

  // Handle retry button click
  const handleRetry = () => {
    if (orderId) {
      retryMutation.mutate(orderId)
    }
  }

  // Load session on mount (optional - order detail doesn't require postcode)
  useEffect(() => {
    const savedSession = sessionStorage.getItem('engineerSession')
    if (savedSession) {
      setSession(JSON.parse(savedSession))
    }
  }, [])

  // Loading state
  if (isLoading) {
    return (
      <EngineerLayout>
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary" />
        </div>
      </EngineerLayout>
    )
  }

  // Error or order not found
  if (error || !order) {
    return (
      <EngineerLayout>
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700 text-sm font-medium mb-2">Order not found</p>
          <button
            onClick={() => navigate(`/${tenantSlug}/engineer/orders`)}
            className="text-brand-primary text-sm font-medium"
          >
            ← Back to Orders
          </button>
        </div>
      </EngineerLayout>
    )
  }

  // Handle cancel order click
  const handleCancelClick = () => {
    if (order?.status === 'awaiting_approval') {
      // Order is pending approval - show contact manager message
      setShowContactManagerModal(true)
    } else {
      // Order is with supplier - show branch contact modal
      setShowCancelModal(true)
    }
  }

  // Status configuration - compact pill with colored dot.
  const getStatusConfig = (status: EngineerOrder['status']) => {
    const label = getStatusLabel(status)
    switch (status) {
      case 'awaiting_approval':
        return { label, color: 'bg-amber-50 text-amber-700', dotColor: 'bg-amber-500' }
      // "Submitting" — retry loop while the order is being pushed to the
      // supplier; own sky colour distinct from "Sent to supplier" (blue).
      case 'started_processing':
        return { label, color: 'bg-sky-50 text-sky-700', dotColor: 'bg-sky-500' }
      case 'approved':
      case 'awaiting_supplier':
        return { label, color: 'bg-blue-50 text-blue-700', dotColor: 'bg-blue-500' }
      case 'processing':
        return { label, color: 'bg-indigo-50 text-indigo-700', dotColor: 'bg-indigo-500' }
      case 'out_for_delivery':
        return { label, color: 'bg-purple-50 text-purple-700', dotColor: 'bg-purple-500' }
      case 'ready_to_collect':
      case 'completed':
        return { label, color: 'bg-green-50 text-green-700', dotColor: 'bg-green-500' }
      case 'submission_failed':
      case 'internal_decline':
      case 'fulfillment_declined':
      case 'supplier_cancelled':
        return { label, color: 'bg-red-50 text-red-700', dotColor: 'bg-red-500' }
      case 'withdrawn':
        return { label, color: 'bg-gray-100 text-gray-500', dotColor: 'bg-gray-400' }
      case 'cancelled':
        return { label, color: 'bg-gray-100 text-gray-700', dotColor: 'bg-gray-500' }
      default:
        return { label, color: 'bg-gray-100 text-gray-700', dotColor: 'bg-gray-500' }
    }
  }

  const statusConfig = order ? getStatusConfig(order.status) : null

  return (
    <EngineerLayout hideHeader>
      {/* Header — themed via `.bg-nav` / `.text-nav`. Shared EngineerHeader
       * suppressed: only the back+title bar renders. */}
      <div className="bg-nav text-nav border-b border-nav-divider sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(`/${tenantSlug}/engineer/orders`)}
              className="p-2 -ml-2 text-nav hover:bg-nav-hover rounded-xl transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-nav font-bold">Order #{order.orderNumber}</h1>
            {/* Copy is offered only while the order is awaiting approval, so the
                engineer can share it with an approver (APR-006 AC3). */}
            {order.status === 'awaiting_approval' && (
              <button
                type="button"
                onClick={handleCopyOrder}
                className="p-1 rounded-md hover:bg-nav-hover active:scale-95 transition-all"
                title="Copy order details"
                aria-label="Copy order details"
              >
                {copied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5 text-nav-inactive" />}
              </button>
            )}
          </div>
        </div>
      </div>

      <main className="max-w-3xl mx-auto px-3 sm:px-4 py-4 sm:py-6 pb-24">
        {/* Status pill */}
        {statusConfig && (
          <div className="flex justify-center py-3 sm:py-4">
            <span
              className={clsx(
                'inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium sm:px-5 sm:py-2.5 sm:text-base',
                statusConfig.color
              )}
            >
              <span className={clsx('h-2 w-2 rounded-full', statusConfig.dotColor)} />
              {statusConfig.label}
            </span>
          </div>
        )}

        {/* Warning banner - shown standalone when the viewer cannot
            manually update status (e.g. not the order placer). When the
            viewer is the placer, the same warning is folded into the
            manual-update card below per docs/UI/image 1.png. */}
        {backendOrder?.supplier_order_id && backendOrder?.status_available === false && backendOrder?.status === 'processing' && backendOrder?.status_warning_message && !canManuallyUpdateStatus && (
          <div className="mb-3 sm:mb-4 bg-white rounded-xl sm:rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="bg-gray-50 px-3 sm:px-4 py-3 flex items-start gap-2">
              <AlertCircle className="mt-0.5 h-4 w-4 text-gray-500 flex-shrink-0" />
              <p className="text-[11px] sm:text-xs text-gray-500">
                {backendOrder.status_warning_message}
              </p>
            </div>
          </div>
        )}

        {/* Manual status update - white warning row with a gray action tray
            and two separate rounded white buttons below, per
            docs/UI/image 1.png and image (1).png. Only shown to the
            engineer who placed the order when the supplier has no live
            status API. Buttons stay disabled while the mutation is in
            flight; the confirm modal carries the spinner. */}
        {canManuallyUpdateStatus && (
          <div className="mb-3 sm:mb-4 bg-white rounded-xl sm:rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            {backendOrder?.status_warning_message && (
              <div className="bg-gray-50 px-3 sm:px-4 py-2 sm:py-2.5 flex items-start gap-2 border-b border-gray-100">
                <AlertCircle className="mt-0.5 h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                <p className="text-[11px] text-gray-500 leading-relaxed">
                  {backendOrder.status_warning_message}
                </p>
              </div>
            )}
            <div className="bg-white px-2.5 sm:px-3 py-2 sm:py-2.5">
              <div className="rounded-lg bg-gray-100 p-1">
                <div className="grid grid-cols-2 gap-1.5">
                  <button
                    onClick={() => {
                      setManualStatusError(null)
                      setManualStatusAction('completed')
                    }}
                    disabled={manualStatusMutation.isPending}
                    className="h-8 flex items-center justify-center gap-1.5 bg-white rounded-md shadow-sm px-3 text-[11px] font-semibold text-gray-900 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                    Completed
                  </button>
                  <button
                    onClick={() => {
                      setManualStatusError(null)
                      setManualStatusAction('fulfillment_declined')
                    }}
                    disabled={manualStatusMutation.isPending}
                    className="h-8 flex items-center justify-center gap-1.5 bg-white rounded-md shadow-sm px-3 text-[11px] font-semibold text-gray-900 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <XCircle className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
                    Declined
                  </button>
                </div>
              </div>
              {manualStatusError && (
                <p className="mt-3 text-xs text-red-600">{manualStatusError}</p>
              )}
            </div>
          </div>
        )}

        {/* Awaiting approval (mock: Pending Approval card — shown near the top) */}
        {order.status === 'awaiting_approval' && (
          <div className="bg-white border border-gray-200 rounded-xl sm:rounded-2xl shadow-sm overflow-hidden mb-3 sm:mb-4">
            <div className="bg-amber-50 px-3 sm:px-4 py-2.5 border-b border-amber-100">
              <h3 className="text-amber-700 font-bold text-xs sm:text-sm">
                {getStatusLabel('awaiting_approval')}
              </h3>
            </div>
            <div className="px-3 sm:px-4 py-3 space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-500">Sent to</span>
                </div>
                <span className="text-gray-900 font-semibold">
                  {order.assigned_approver || '—'}
                </span>
              </div>
              <p className="text-gray-500 text-xs">
                {order.assigned_approver
                  ? "Your approver will review and approve or decline this order. You'll be notified by email."
                  : 'You will be notified once it has been approved or declined.'}
              </p>
              {/* Withdraw Order (APR-007) — only shown while awaiting approval */}
              <button
                type="button"
                onClick={() => { setWithdrawError(null); setShowWithdrawDialog(true) }}
                className="w-full text-center text-xs font-medium text-gray-500 border border-gray-200 rounded-lg px-3 py-2 transition-colors hover:text-red-600 hover:border-red-200"
              >
                Withdraw order
              </button>
            </div>
          </div>
        )}

        {/* Internal decline (mock: "Declined by <approver>" card — near the top) */}
        {order.status === 'internal_decline' && (
          <div className="bg-white border border-gray-200 rounded-xl sm:rounded-2xl shadow-sm overflow-hidden mb-3 sm:mb-4">
            <div className="bg-red-50 px-3 sm:px-4 py-2.5 border-b border-red-100">
              <h3 className="text-red-700 font-bold text-xs sm:text-sm">
                Declined{(order.approved_by || order.assigned_approver) ? ` by ${order.approved_by || order.assigned_approver}` : ''}
              </h3>
            </div>
            {order.disapprovalReason && (
              <div className="px-3 sm:px-4 py-3 text-sm text-gray-700">
                {order.disapprovalReason}
              </div>
            )}
          </div>
        )}

        {/* Order Submission Status - Shows retry info if applicable */}
        {backendOrder && (
          <div className="mb-3 sm:mb-4">
            <OrderSubmissionStatus
              order={backendOrder}
              onRetry={handleRetry}
              isRetrying={retryMutation.isPending}
              isRetryInProgress={isRetryPolling}
            />
          </div>
        )}

        {/* Order Information */}
        <div className="mb-3 sm:mb-4 bg-white rounded-xl sm:rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="bg-gray-50 px-3 sm:px-4 py-2.5 sm:py-3 border-b border-gray-200">
            <h2 className="text-gray-900 font-bold text-xs sm:text-sm">Order information</h2>
          </div>
          <div className="px-3 sm:px-4 py-3 sm:py-4 space-y-2.5 text-xs sm:text-sm">
            {/* The numbers the branch counter asks for. Supplier order number
                only exists once the supplier has accepted the order. */}
            {/* Always shown, at every stage; "Not issued yet" until the supplier
                issues one (with the "quote our number" hint only while open). */}
            <SupplierOrderNumber
              supplierOrderId={backendOrder?.supplier_order_id}
              ticketNumber={backendOrder?.supplier_ticket_number}
              isOpenWithSupplier={isOpenWithSupplier}
            />

            {/* At every stage, but only once an admin has entered the supplier's
                account number in Django admin — no label or dash before then
                (mock: "left out, no blank or dash"). */}
            {backendOrder?.supplier_account_number && (
              <div>
                <p className="text-gray-500 text-[11px] sm:text-xs mb-0.5">Account number</p>
                <p className="text-gray-900 font-medium break-all">{backendOrder.supplier_account_number}</p>
              </div>
            )}

            <div>
              <p className="text-gray-500 text-[11px] sm:text-xs mb-0.5">Submitted at</p>
              <p className="text-gray-900 font-medium">
                {order.submittedAt
                  ? formatDateTime(order.submittedAt)
                  : order.supplierOrderId
                    ? formatDateTime(order.placedAt)
                    : EMPTY_VALUE}
              </p>
            </div>

            {backendOrder?.placed_by_name && (
              <div>
                <p className="text-gray-500 text-[11px] sm:text-xs mb-0.5">Ordered by</p>
                <p className="text-gray-900 font-medium">{backendOrder.placed_by_name}</p>
              </div>
            )}

            <div>
              <p className="text-gray-500 text-[11px] sm:text-xs mb-0.5">Supplier</p>
              <p className="text-gray-900 font-medium">
                {order.supplierName}{order.supplierIsActive === false && <span className="text-red-500 text-xs ml-1">(Inactive)</span>} {order.branchName}
              </p>
            </div>

            <div>
              <p className="text-gray-500 text-[11px] sm:text-xs mb-0.5">Collection or delivery</p>
              {order.fulfillmentType === 'pickup' ? (
                <div className="flex items-center gap-1.5">
                  <Package className="w-3.5 h-3.5 text-gray-400" />
                  <p className="text-gray-900 font-medium">Collection</p>
                </div>
              ) : (
                <div className="flex items-center gap-1.5">
                  <Truck className="w-3.5 h-3.5 text-gray-400" />
                  <p className="text-gray-900 font-medium">Delivery</p>
                </div>
              )}
              {/* Collection guidance (DGM-003) — backend decides visibility. */}
              {order.collectionGuidance && (
                <p className="text-blue-600 mt-1 leading-snug">{order.collectionGuidance.text}</p>
              )}
              {/* Delivery guidance (DGM-004) — backend decides visibility. */}
              {order.deliveryGuidance && (
                <p className="text-blue-600 mt-1 leading-snug">{order.deliveryGuidance.text}</p>
              )}
            </div>

            {!FULFILLMENT_DATE_HIDE_STATUSES.includes(order.status) && (
              <div>
                <p className="text-gray-500 text-[11px] sm:text-xs mb-0.5">
                  {order.fulfillmentType === 'pickup' ? 'Collection date' : 'Delivery date'}
                </p>
                <p className="text-gray-900 font-medium">
                  {bookedDateKey ? formatShortDate(bookedDateKey) : 'To be confirmed by the branch'}
                </p>
                {bookedDateKey && order.isFulfillmentDateUnconfirmed && (
                  <p className="text-gray-500 text-xs mt-0.5">To be confirmed by the branch</p>
                )}
              </div>
            )}

            {order.fulfillmentType === 'delivery' && order.deliveryAddress && (
              <div>
                <p className="text-gray-500 text-[11px] sm:text-xs mb-0.5">Delivery address</p>
                <p className="text-gray-900 font-medium">
                  {[
                    order.deliveryAddress.line_1,
                    order.deliveryAddress.line_2,
                    order.deliveryAddress.line_3,
                    order.deliveryAddress.post_town,
                    order.deliveryAddress.postcode,
                  ]
                    .filter(Boolean)
                    .join(', ')}
                </p>
              </div>
            )}

            {order.branch && (
              <div>
                {/* Mock's branch row: the name + address itself is the link,
                    opening Google Maps with directions from the engineer's
                    current location. Plain row when there's nothing to link. */}
                {branchDirectionsUrl ? (
                  <a
                    href={branchDirectionsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="-mx-1.5 flex min-h-[44px] items-center gap-3 rounded-lg px-1.5 transition-colors hover:bg-gray-50"
                  >
                    <BranchRowContent name={branchRowName} address={branchAddressLine} />
                    <ExternalLink className="h-4 w-4 flex-shrink-0 text-gray-400" aria-hidden />
                    <span className="sr-only">, open in maps (new tab)</span>
                  </a>
                ) : (
                  <div className="-mx-1.5 flex min-h-[44px] items-center gap-3 px-1.5">
                    <BranchRowContent name={branchRowName} address={branchAddressLine} />
                  </div>
                )}
                {/* Suppliers that never report "ready" (Travis Perkins,
                    CEF): tell the engineer to ring while the order is with
                    them. Same "with the supplier and still open" rule as the
                    collection hint above, so the two always agree. */}
                {showCallBranchPrompt && order.branch.phone && (
                  // Mock's highlighted strip: its banana-split / goldfish colours
                  // are exactly Tailwind amber-100 / orange-600.
                  <p className="mt-3 flex gap-2 rounded-lg bg-amber-100 px-3 py-2 text-sm text-gray-900">
                    <PhoneCall className="mt-0.5 h-4 w-4 flex-shrink-0 text-orange-600" aria-hidden />
                    <span className="min-w-0 break-words">
                      {order.fulfillmentType === 'pickup'
                        ? `${order.supplierName} doesn't tell us when orders are ready. Call before you set off.`
                        : `${order.supplierName} doesn't tell us when orders are out for delivery. Call to check on it.`}
                    </span>
                  </p>
                )}
                {/* Every supplier: one tap to ring the branch, once it has the order. */}
                {order.branch.phone && !NOT_WITH_BRANCH_STATUSES.includes(order.status) && (
                  <Button asChild className="mt-3 w-full">
                    <a href={`tel:${order.branch.phone}`}>
                      <Phone className="w-4 h-4" />
                      Call branch
                      <span className="font-medium tabular-nums">{order.branch.phone}</span>
                    </a>
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Job Details — always shown, since the job number row always is
            ("—" when the engineer didn't enter one; it's optional by default). */}
        <div className="mb-3 sm:mb-4 bg-white rounded-xl sm:rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="bg-gray-50 px-3 sm:px-4 py-2.5 sm:py-3 border-b border-gray-200">
            <h2 className="text-gray-900 font-bold text-xs sm:text-sm">Job details</h2>
          </div>
          <div className="px-3 sm:px-4 py-3 sm:py-4 space-y-2.5 text-xs sm:text-sm">
            <div>
              <p className="text-gray-500 text-[11px] sm:text-xs mb-0.5">Job number</p>
              <p className="text-gray-900 font-medium break-all">{order.jobNumber || EMPTY_VALUE}</p>
            </div>
            {order.reasonForOrder && (
              <div>
                <p className="text-gray-500 text-[11px] sm:text-xs mb-0.5">Reason</p>
                <p className="text-gray-900 font-medium">{order.reasonForOrder}</p>
              </div>
            )}
            {order.secondaryReason && (
              <div>
                <p className="text-gray-500 text-[11px] sm:text-xs mb-0.5">Secondary reason</p>
                <p className="text-gray-900 font-medium">{order.secondaryReason}</p>
              </div>
            )}
          </div>
        </div>

        {/* Items */}
        <div className="mb-3 sm:mb-4 bg-white rounded-xl sm:rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="bg-gray-50 px-3 sm:px-4 py-2.5 sm:py-3 border-b border-gray-200">
            <h2 className="text-gray-900 font-bold text-xs sm:text-sm">
              Items ({order.items.length})
            </h2>
          </div>
          <div className="divide-y divide-gray-100">
            {order.items.map((item) => {
              // Hide per-item availability on orders that never went ahead
              // (supplier declined/cancelled) — the dates are moot there.
              const availability = HIDE_ITEM_AVAILABILITY_STATUSES.includes(order.status)
                ? null
                : getItemAvailability(item)
              return (
              <div key={item.productId} className="px-3 sm:px-4 py-2.5 sm:py-3 flex gap-3">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {item.imageUrl ? (
                    <img
                      src={item.imageUrl}
                      alt={item.productName}
                      className="w-full h-full object-contain"
                      onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextElementSibling?.classList.remove('hidden') }}
                    />
                  ) : null}
                  <div className={item.imageUrl ? 'hidden flex flex-col items-center justify-center' : 'flex flex-col items-center justify-center'}>
                    <Package className="w-5 h-5 text-gray-300" />
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <h4 className="text-gray-900 font-medium text-xs sm:text-sm mb-0.5 sm:mb-1">
                    {item.productName}
                  </h4>
                  {item.isVanStock && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-violet-100 text-violet-700 border border-violet-300 mb-1">
                      <Package className="w-2.5 h-2.5" />
                      Van stock
                    </span>
                  )}
                  {item.manufacturerCode && (
                    <p className="text-gray-400 text-[11px] sm:text-xs mb-0.5">Product code: {item.manufacturerCode}</p>
                  )}
                  {/* Brand (DEV-51596) — always shown with a "-" fallback
                      per AC2. Order Detail is a condensed line view with no
                      product-info accordion (BM-015), so Brand sits inline
                      next to Product code — the metadata row engineers
                      already read on these lines. */}
                  <p className="text-gray-400 text-[11px] sm:text-xs mb-1">Brand: {item.brand || '-'}</p>

                  <div className="flex items-center justify-between gap-3 text-[11px] sm:text-xs text-gray-500">
                    <span>
                      {formatUnitPrice(item.price)} × {item.quantity}
                    </span>
                    <span className="text-gray-900 font-bold text-xs sm:text-sm flex-shrink-0">
                      £{item.lineTotal.toFixed(2)}
                    </span>
                  </div>
                  {availability && (
                    <div className="mt-1.5">
                      {availability.kind === 'oos' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] sm:text-[11px] font-semibold bg-red-100 text-red-700 border border-red-300">
                          <XCircle className="w-2.5 h-2.5" />
                          Out of stock
                        </span>
                      )}
                      {availability.kind === 'changed' && (
                        <span className="inline-flex items-center gap-1 flex-wrap text-[11px] sm:text-xs font-medium text-amber-700">
                          <AlertCircle className="w-3 h-3 flex-shrink-0" />
                          Date changed:
                          {availability.original && (
                            <span className="text-gray-400 line-through">{availability.original}</span>
                          )}
                          <span className="text-amber-700 font-semibold">{availability.label}</span>
                        </span>
                      )}
                      {availability.kind === 'ready' && (
                        <span className="inline-flex items-center gap-1 text-[11px] sm:text-xs font-medium text-emerald-700">
                          <CheckCircle className="w-3 h-3 flex-shrink-0" />
                          Available {availability.label}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
              )
            })}
          </div>
          <div className="border-t-2 border-gray-200 px-3 sm:px-4 py-3 sm:py-4 flex justify-between items-center">
            <span className="text-gray-900 font-bold text-sm">Order total</span>
            <span className="text-gray-900 font-bold text-lg sm:text-xl">
              £{order.total.toFixed(2)}
            </span>
          </div>
        </div>

        {/* Order again (APR-008; "Add Items to Basket" before the mock rename) — available on any order status.
            Reorder needs the engineer's current location to pick the branch, so
            it requires a configured postcode. */}
        <button
          type="button"
          onClick={() => {
            if (!session || session.latitude == null || session.longitude == null) {
              addToast('Set your postcode first to reorder.', 'warning')
              return
            }
            reorderMutation.mutate(orderId!)
          }}
          disabled={reorderMutation.isPending}
          className="mt-3 sm:mt-4 w-full flex items-center justify-center gap-2 text-sm font-semibold text-gray-900 bg-white hover:bg-gray-50 border border-gray-200 shadow-sm rounded-xl px-4 py-3 transition-colors disabled:opacity-60"
        >
          {reorderMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingCart className="w-4 h-4" />}
          Order again
        </button>

        {/* CANCEL-001/002: Cancel order section — eligibility matrix driven
            by `order.cancelEntry` (null once a request has been sent, so this
            automatically disappears when the Cancellation requested card
            shows above). */}
        {order.cancelEntry && (
          <CancelOrderSection
            entry={order.cancelEntry}
            branchPhone={order.branch?.phone || ''}
            onRequest={
              order.cancelEntry === 'request'
                ? () => {
                    setCancelSendFailed(false)
                    setShowCancelSheet(true)
                  }
                : undefined
            }
          />
        )}
        {/* CANCEL-002: Cancellation requested card. Shown under the items card
            once a request has been persisted. "Branch confirmed? Mark as
            cancelled" button is wired up in CANCEL-004. */}
        {order.cancellationRequest && (
          <CancelRequestCard
            request={{
              reasonLabel: order.cancellationRequest.reasonLabel,
              note: order.cancellationRequest.note,
              sentAt: order.cancellationRequest.sentAt,
              cc: order.cancellationRequest.cc,
              confirmed: order.cancellationRequest.confirmed,
            }}
            supplierName={order.supplierName}
            branchName={order.branchName}
            branchPhone={order.branch?.phone || ''}
            focusOnMount={focusCancelCard}
            onMarkCancelled={
              order.cancellationRequest.confirmed
                ? undefined
                : () => setShowMarkCancelled(true)
            }
          />
        )}

        {/* Activity (DEV-49xxx): the approver's timeline, same events and
            wording, on the engineer's own order. Collapsed to the latest few
            events so it never buries the rest of the page on a phone. */}
        <div className="mt-3 sm:mt-4 bg-white rounded-xl sm:rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="bg-gray-50 px-3 sm:px-4 py-2.5 sm:py-3 border-b border-gray-200">
            <h2 className="text-gray-900 font-bold text-xs sm:text-sm">Activity</h2>
          </div>
          <div className="px-3 sm:px-4 py-3">
            {activityLoading ? (
              <div className="space-y-2.5">
                {[0, 1, 2].map((row) => (
                  <div key={row} className="h-3 w-2/3 animate-pulse rounded bg-gray-100" />
                ))}
              </div>
            ) : activityError ? (
              <p className="text-sm text-gray-500">Couldn't load the activity log.</p>
            ) : activityEvents.length === 0 ? (
              <p className="text-sm text-gray-500">No activity yet.</p>
            ) : (
              <ol className="relative">
                {visibleActivityEvents.map((event, index) => {
                  const isLast = index === visibleActivityEvents.length - 1
                  return (
                    <li key={index} className="relative flex gap-3 pb-4 last:pb-0">
                      <div className="relative flex flex-col items-center">
                        <span className="mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-brand-primary" />
                        {!isLast && <span className="w-px flex-1 bg-gray-200" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm leading-snug text-gray-900 break-words">{event.message}</p>
                        {event.details && event.details.length > 0 && (
                          <ul className="mt-1.5 space-y-1">
                            {event.details.map((detail, detailIndex) => (
                              <li key={detailIndex} className="flex gap-1.5 text-xs leading-snug text-gray-700">
                                <span className="text-gray-400">&bull;</span>
                                <span className="min-w-0 break-words">{detail}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                        {event.timestamp && (
                          <p className="mt-1.5 text-[11px] uppercase tracking-wide text-gray-400">
                            {formatActivityTimestamp(event.timestamp)}
                          </p>
                        )}
                      </div>
                    </li>
                  )
                })}
              </ol>
            )}
          </div>
          {activityEvents.length > COLLAPSED_ACTIVITY_COUNT && (
            <button
              type="button"
              onClick={() => setShowAllActivity((isOpen) => !isOpen)}
              aria-expanded={showAllActivity}
              className="flex min-h-[44px] w-full items-center justify-center gap-1.5 border-t border-gray-100 px-3 py-2.5 text-sm font-semibold text-brand-primary transition-colors hover:bg-gray-50 active:bg-gray-100"
            >
              {showAllActivity ? 'Show less' : `Show all activity (${activityEvents.length})`}
              {showAllActivity ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
          )}
        </div>

        {/* Cancel Order Button - hidden for now */}
      </main>

      {/* Cancel Order Modal - Contact Branch */}
      <Dialog.Root open={showCancelModal} onOpenChange={setShowCancelModal}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
          <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[90vw] max-w-md bg-white rounded-xl border border-gray-200 shadow-xl p-4 sm:p-5">
            <div className="flex items-center gap-3 mb-4">
              <XCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
              <Dialog.Title className="text-gray-900 font-bold text-xl">
                Cancel order
              </Dialog.Title>
            </div>

            <div className="bg-red-50 rounded-xl border border-red-200 p-3 sm:p-4 mb-5">
              <p className="text-red-900 text-sm leading-relaxed mb-3">
                To cancel this order, please contact the supplier branch directly:
              </p>
              {order.branch && (
                <div className="space-y-2">
                  <p className="text-gray-900 font-semibold text-sm">
                    {order.supplierName}{order.supplierIsActive === false && <span className="text-red-500 text-xs ml-1">(Inactive)</span>} - {order.branchName}
                  </p>
                  <a
                    href={`tel:${order.branch.phone}`}
                    className="flex items-center gap-2 text-brand-primary text-sm font-medium"
                  >
                    <Phone className="w-4 h-4" />
                    {order.branch.phone}
                  </a>
                </div>
              )}
            </div>

            <Button
              variant="outline"
              onClick={() => setShowCancelModal(false)}
              className="w-full"
            >
              Close
            </Button>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Withdraw Confirmation Dialog (APR-007 AC2) */}
      <Dialog.Root open={showWithdrawDialog} onOpenChange={(open) => { if (!withdrawMutation.isPending) { setShowWithdrawDialog(open); if (!open) setWithdrawError(null) } }}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
          <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[90vw] max-w-md bg-white rounded-xl border border-gray-200 shadow-xl p-4 sm:p-5">
            <Dialog.Title className="text-gray-900 font-bold text-lg">
              Withdraw order?
            </Dialog.Title>
            <Dialog.Description className="text-gray-500 text-sm mt-2 leading-relaxed">
              This will cancel your approval request. The order will not be placed with the supplier.
            </Dialog.Description>

            {withdrawError && (
              <p className="mt-3 text-xs text-red-600">{withdrawError}</p>
            )}

            <div className="flex justify-end gap-2 mt-5">
              <button
                type="button"
                onClick={() => setShowWithdrawDialog(false)}
                disabled={withdrawMutation.isPending}
                className="text-sm font-medium text-gray-500 hover:text-gray-700 px-3 py-1.5 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => withdrawMutation.mutate(orderId!)}
                disabled={withdrawMutation.isPending}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg px-3 py-1.5 transition-colors disabled:opacity-60"
              >
                {withdrawMutation.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Withdraw order
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* CANCEL-002: Cancellation request bottom sheet. The sheet owns the
          reason/note form; the mutation lives in this component so success
          invalidates the order detail + list caches and the Cancellation
          requested card renders in its place. */}
      <CancelRequestSheet
        open={showCancelSheet}
        onClose={() => {
          setShowCancelSheet(false)
          setCancelSendFailed(false)
        }}
        onSubmit={(reason, note) =>
          requestCancellationMutation.mutate({ reason, note })
        }
        isSending={requestCancellationMutation.isPending}
        isFailed={cancelSendFailed}
        supplierName={order.supplierName}
        branchName={order.branchName}
        branchEmail={order.cancellationContactEmail || order.branch?.email || ''}
        branchPhone={order.branch?.phone || ''}
        orderNumber={order.orderNumber}
        supplierOrderNumber={order.supplierOrderId}
        accountNumber={order.supplierAccountNumber || ''}
        jobNumber={order.jobNumber || ''}
        itemCount={order.items.reduce(
          (sum, item) => sum + (item.quantity || 0),
          0
        )}
        totalAmount={order.total}
        fulfillmentMode={order.fulfillmentType === 'delivery' ? 'delivery' : 'pickup'}
        deliveryAddress={
          order.deliveryAddress
            ? [
                order.deliveryAddress.line_1,
                order.deliveryAddress.line_2,
                order.deliveryAddress.line_3,
                order.deliveryAddress.post_town,
                order.deliveryAddress.postcode,
              ]
                .filter(Boolean)
                .join(', ')
            : null
        }
        engineerName={
          meData
            ? `${meData.first_name ?? ''} ${meData.last_name ?? ''}`.trim() ||
              meData.email ||
              ''
            : ''
        }
        engineerEmail={meData?.email || ''}
      />

      {/* CANCEL-004: confirm dialog for marking the order Cancelled after
          the branch confirms off-platform. Only reachable from the
          CancelRequestCard's "Branch confirmed? Mark as cancelled" button. */}
      <MarkCancelledDialog
        open={showMarkCancelled}
        onClose={() => setShowMarkCancelled(false)}
        onConfirm={() => markCancelledMutation.mutate()}
        isPending={markCancelledMutation.isPending}
      />

      {/* Contact Manager Modal */}
      <Dialog.Root open={showContactManagerModal} onOpenChange={setShowContactManagerModal}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
          <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[90vw] max-w-md bg-white rounded-xl border border-gray-200 shadow-xl p-4 sm:p-5">
            <div className="flex items-center gap-3 mb-4">
              <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0" />
              <Dialog.Title className="text-gray-900 font-bold text-xl">
                Cannot cancel order
              </Dialog.Title>
            </div>

            <div className="bg-amber-50 rounded-xl border border-amber-200 p-3 sm:p-4 mb-5">
              <p className="text-amber-900 text-sm leading-relaxed">
                This order is awaiting approval. Please contact your approver to cancel this order.
              </p>
            </div>

            <Button
              variant="outline"
              onClick={() => setShowContactManagerModal(false)}
              className="w-full"
            >
              Close
            </Button>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Manual status confirmation modal */}
      <Dialog.Root
        open={manualStatusAction !== null}
        onOpenChange={(open) => {
          // Lock the modal while the mutation is in flight (and while we
          // wait for the cache refetch) so the spinner stays visible.
          if (!open && !manualStatusMutation.isPending) {
            setManualStatusAction(null)
          }
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
          <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[90vw] max-w-md bg-white rounded-xl border border-gray-200 shadow-xl p-4 sm:p-5">
            <div className="flex items-center gap-3 mb-4">
              {manualStatusAction === 'completed' ? (
                <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
              ) : (
                <XCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
              )}
              <Dialog.Title className="text-gray-900 font-bold text-xl">
                {manualStatusAction === 'completed' ? 'Mark as Completed?' : 'Mark as Declined?'}
              </Dialog.Title>
            </div>

            <p className="text-gray-700 text-sm leading-relaxed mb-5">
              {manualStatusAction === 'completed'
                ? `This confirms you collected or received the items. The order will be set to ${getStatusLabel('completed')}.`
                : `The order will be set to ${getStatusLabel('fulfillment_declined')}. This indicates it was not fulfilled.`}
            </p>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setManualStatusAction(null)}
                disabled={manualStatusMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  if (orderId && manualStatusAction) {
                    manualStatusMutation.mutate({
                      id: orderId,
                      newStatus: manualStatusAction,
                    })
                  }
                }}
                disabled={manualStatusMutation.isPending}
              >
                {manualStatusMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Yes, proceed'
                )}
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </EngineerLayout>
  )
}
